import React, { createContext, useState, useEffect, useContext, useCallback, useRef } from 'react';
import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  signInWithPopup, 
  signOut as firebaseSignOut,
  onAuthStateChanged,
  updateProfile,
  sendPasswordResetEmail,
  sendEmailVerification
} from 'firebase/auth';
import { auth, provider as googleProvider } from '../firebaseConfig';
import { useDomain } from './DomainContext';
import { BACKEND_URL } from '../config';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isRegistering, setIsRegistering] = useState(false);
  const [needsProjectRegistration, setNeedsProjectRegistration] = useState(false);
  
  // Ref per tracciare l'ultimo progetto fetchato
  const lastFetchedProjectRef = useRef(null);

  // Hook per accedere al contesto del dominio
  const { config: domainConfig, loading: domainLoading, currentDomain } = useDomain();

  // ============= UTILITY FUNCTIONS =============
  
  const clearAuthData = useCallback(() => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userProfile');
    localStorage.removeItem('lastProjectId');
    setUser(null);
    setUserProfile(null);
    lastFetchedProjectRef.current = null;
    // NON tocchiamo loading qui - viene gestito dagli effect
  }, []);

  const saveAuthData = useCallback((token, profile) => {
    if (token) {
      localStorage.setItem('authToken', token);
    }
    if (profile) {
      localStorage.setItem('userProfile', JSON.stringify(profile));
      if (profile.project_id) {
        localStorage.setItem('lastProjectId', profile.project_id);
      }
    }
  }, []);

  // ============= TOKEN MANAGEMENT =============
  
  const getValidToken = useCallback(async (forceRefresh = false) => {
    if (!user) {
      console.log('[AuthContext] Token request failed - no user');
      return null;
    }

    try {
      const token = await user.getIdToken(forceRefresh);
      return token;
    } catch (error) {
      console.error('[AuthContext] Token retrieval error:', error);
      return null;
    }
  }, [user]);

  // ============= AUTHENTICATION METHODS =============

  const login = useCallback(async (email, password) => {
    try {
      setLoading(true);
      setError(null);

      console.log('[AuthContext] Login attempt:', email);

      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const firebaseUser = userCredential.user;
      
      const token = await firebaseUser.getIdToken();
      saveAuthData(token, null);
      
      // Setta l'utente nello state
      setUser(firebaseUser);
      
      // IMPORTANTE: Fetcha il profilo per il progetto corrente
      console.log('[AuthContext] Login successful, fetching profile...');
      const profile = await fetchUserProfile(firebaseUser);
      
      if (!profile) {
        console.log('[AuthContext] User authenticated but no profile for this project');
        // L'utente è autenticato ma non ha un profilo per questo progetto
        // Potrebbe dover completare la registrazione per il progetto
        setNeedsProjectRegistration(true);
      }
      
      return { user: firebaseUser, profile };

    } catch (error) {
      console.error('[AuthContext] Login error:', error);
      clearAuthData();
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [saveAuthData, clearAuthData]);

  const googleLogin = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('[AuthContext] Google login attempt');

      const result = await signInWithPopup(auth, googleProvider);
      const firebaseUser = result.user;
      
      const token = await firebaseUser.getIdToken();
      saveAuthData(token, null);

      // Setta l'utente nello state
      setUser(firebaseUser);
      
      // Fetcha il profilo per il progetto corrente
      console.log('[AuthContext] Google login successful, fetching profile...');
      let profile = await fetchUserProfile(firebaseUser);
      
      if (!profile) {
        console.log('[AuthContext] No profile found, attempting auto-registration for Google user');
        // Auto-registrazione per utenti Google
        const registrationResult = await registerForCurrentProject();
        if (registrationResult?.profile) {
          profile = registrationResult.profile;
        } else {
          setNeedsProjectRegistration(true);
        }
      }
      
      return { user: firebaseUser, profile };

    } catch (error) {
      console.error('[AuthContext] Google login error:', error);
      clearAuthData();
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [saveAuthData, clearAuthData]);

  const register = useCallback(async (email, password, displayName = '') => {
    // Validazione pre-registrazione
    if (domainLoading) {
      throw new Error('Configurazione dominio in caricamento. Riprova tra poco.');
    }

    const projectId = domainConfig?.project_id;
    const domainName = domainConfig?.domain_name || currentDomain;

    if (!projectId) {
      console.error('[AuthContext] Registration blocked - missing project_id');
      throw new Error('Configurazione progetto non valida.');
    }

    try {
      setLoading(true);
      setError(null);
      setIsRegistering(true);

      // Step 1: Crea utente in Firebase
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const firebaseUser = userCredential.user;

      // Step 2: Aggiorna il profilo Firebase
      if (displayName) {
        await updateProfile(firebaseUser, { displayName });
      }

      // Step 3: Ottieni il token
      const token = await firebaseUser.getIdToken();

      // Step 4: Registra nel backend
      const response = await fetch(`${BACKEND_URL}/api/user/register-project`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-Domain-Name': domainName,
          'X-Project-ID': projectId,
          'X-Firebase-UID': firebaseUser.uid
        },
        body: JSON.stringify({
          user_auth_provider_id: firebaseUser.uid,
          user_email: firebaseUser.email,
          user_display_name: displayName || firebaseUser.displayName || email.split('@')[0],
          user_photo_url: firebaseUser.photoURL,
          domain_name: domainName,
          auth_provider_type: 'email',
          user_locale: navigator.language || 'it-IT'
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        // Elimina l'utente Firebase se la registrazione backend fallisce
        await firebaseUser.delete().catch(console.error);
        throw new Error(errorData.detail || errorData.message || 'Errore registrazione backend');
      }

      const profile = await response.json();
      
      // Step 5: Salva i dati
      saveAuthData(token, profile);
      setUser(firebaseUser);
      setUserProfile(profile);
      lastFetchedProjectRef.current = projectId;

      console.log('[AuthContext] Registration successful');
      return { user: firebaseUser, profile };

    } catch (error) {
      console.error('[AuthContext] Registration error:', error);
      clearAuthData();
      throw error;
    } finally {
      setLoading(false);
      setIsRegistering(false);
    }
  }, [domainConfig, domainLoading, currentDomain, saveAuthData, clearAuthData]);

  const logout = useCallback(async () => {
    try {
      setLoading(true);
      console.log('[AuthContext] Logout initiated');
      
      await firebaseSignOut(auth);
      clearAuthData();
      
      console.log('[AuthContext] Logout successful');
    } catch (error) {
      console.error('[AuthContext] Logout error:', error);
    } finally {
      setLoading(false);
    }
  }, [clearAuthData]);

  const resetPassword = useCallback(async (email) => {
    try {
      await sendPasswordResetEmail(auth, email);
      console.log('[AuthContext] Password reset email sent');
      return true;
    } catch (error) {
      console.error('[AuthContext] Password reset error:', error);
      throw error;
    }
  }, []);

  // ============= HELPER METHODS =============

  const canAccessCurrentProject = useCallback(() => {
    if (!user || !userProfile) return false;
    const currentProjectId = domainConfig?.project_id;
    if (!currentProjectId) return false;
    return userProfile.project_id === currentProjectId;
  }, [user, userProfile, domainConfig]);

  // ============= PROFILE FETCHING (NON in useCallback per evitare dipendenze circolari) =============
  
  // Questa funzione NON è wrapped in useCallback per evitare problemi di dipendenze
  const fetchUserProfile = async (firebaseUser) => {
    if (!firebaseUser) {
      console.log('[AuthContext] Profile fetch skipped - no user');
      return null;
    }

    const projectId = domainConfig?.project_id;
    const domainName = domainConfig?.domain_name || currentDomain;

    if (!projectId) {
      console.log('[AuthContext] Profile fetch skipped - no project_id');
      return null;
    }

    // Evita fetch duplicati per lo stesso progetto
    if (lastFetchedProjectRef.current === projectId && userProfile?.project_id === projectId) {
      console.log('[AuthContext] Profile already fetched for this project');
      return userProfile;
    }

    try {
      const token = await firebaseUser.getIdToken();
      
      // LOG DETTAGLIATO DELLA RICHIESTA
      const requestUrl = `${BACKEND_URL}/api/user/profile`;
      const requestHeaders = {
        'Authorization': `Bearer ${token}`,
        'X-Firebase-UID': firebaseUser.uid,
        'X-Domain-Name': domainName,
        'X-Project-ID': projectId,
        'Content-Type': 'application/json'
      };
      
      console.log('[AuthContext] Making profile request:', {
        url: requestUrl,
        method: 'GET',
        headers: {
          ...requestHeaders,
          'Authorization': 'Bearer [TOKEN HIDDEN]' // Non logghiamo il token per sicurezza
        },
        firebaseUid: firebaseUser.uid,
        projectId,
        domainName
      });

      const response = await fetch(requestUrl, {
        method: 'GET',
        headers: requestHeaders
      });

      console.log('[AuthContext] Profile response status:', response.status);

      if (response.ok) {
        const profile = await response.json();
        console.log('[AuthContext] Profile fetched successfully:', {
          profileId: profile.id,
          projectId: profile.project_id,
          onboardingComplete: profile.is_onboarding_completed
        });
        
        setUserProfile(profile);
        setNeedsProjectRegistration(false);
        saveAuthData(token, profile);
        lastFetchedProjectRef.current = projectId;
        return profile;
      }

      if (response.status === 404) {
        console.log('[AuthContext] Profile not found (404) - user needs registration for project');
        console.log('[AuthContext] Checking if should auto-register...');
        
        // Per Google login, tentiamo auto-registrazione
        if (firebaseUser.providerData?.[0]?.providerId === 'google.com') {
          console.log('[AuthContext] User logged in with Google, attempting auto-registration');
          // Chiamiamo registerUserForProject qui
          // TODO: Implementare auto-registrazione
        }
        
        setNeedsProjectRegistration(true);
        setUserProfile(null);
        lastFetchedProjectRef.current = projectId;
        return null;
      }

      if (response.status === 403) {
        console.log('[AuthContext] Profile access denied (403)');
        const errorData = await response.json().catch(() => ({}));
        console.log('[AuthContext] Error details:', errorData);
        setNeedsProjectRegistration(true);
        setUserProfile(null);
        lastFetchedProjectRef.current = projectId;
        return null;
      }

      throw new Error(`Profile fetch error: ${response.status}`);

    } catch (error) {
      console.error('[AuthContext] Profile fetch error:', error);
      setError(error.message);
      return null;
    }
  };

  // ============= LIFECYCLE MANAGEMENT =============

  // Effect di mount - verifica stato iniziale
  useEffect(() => {
    console.log('[AuthContext] Component mounted, checking initial auth state...');
    const currentUser = auth.currentUser;
    console.log('[AuthContext] Current Firebase user on mount:', {
      hasUser: !!currentUser,
      uid: currentUser?.uid,
      email: currentUser?.email
    });

    // AUTO-LOGIN DI TEST - SOLO PER DEVELOPMENT
    // RIMUOVERE IN PRODUZIONE!
    if (process.env.NODE_ENV === 'development' && !currentUser) {
      const autoLogin = async () => {
        try {
          console.log('[AuthContext] AUTO-LOGIN TEST - Attempting...');
          // CAMBIA CON LE TUE CREDENZIALI DI TEST
          const testEmail = 'test@example.com';
          const testPassword = 'test123456';
          
          const result = await signInWithEmailAndPassword(auth, testEmail, testPassword);
          console.log('[AuthContext] AUTO-LOGIN TEST - Success:', result.user.email);
        } catch (error) {
          console.log('[AuthContext] AUTO-LOGIN TEST - Failed (user might not exist):', error.message);
        }
      };
      
      // Uncommenta la riga sotto per abilitare auto-login
      // autoLogin();
    }
  }, []);

  // Effect 1: Gestisce solo l'auth state di Firebase
  useEffect(() => {
    console.log('[AuthContext] Setting up auth listener');
    
    // Setta loading a false dopo un breve delay per dare tempo a Firebase
    const timeoutId = setTimeout(() => {
      console.log('[AuthContext] Force setting loading to false after timeout');
      setLoading(false);
    }, 2000); // Max 2 secondi di attesa
    
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      console.log('[AuthContext] Auth state changed:', {
        hasUser: !!firebaseUser,
        uid: firebaseUser?.uid,
        email: firebaseUser?.email
      });
      
      clearTimeout(timeoutId); // Cancella il timeout se Firebase risponde
      
      if (firebaseUser) {
        setUser(firebaseUser);
        console.log('[AuthContext] User set in state');
      } else {
        console.log('[AuthContext] No user - clearing auth data');
        setUser(null);
        setUserProfile(null);
        localStorage.removeItem('authToken');
        localStorage.removeItem('userProfile');
        localStorage.removeItem('lastProjectId');
      }
      
      // SEMPRE setta loading a false quando Firebase risponde
      setLoading(false);
      console.log('[AuthContext] Loading set to false');
    });

    return () => {
      clearTimeout(timeoutId);
      unsubscribe();
    };
  }, []); // Nessuna dipendenza

  // Effect 2: Fetcha il profilo quando sia user che domainConfig sono pronti
  useEffect(() => {
    console.log('[AuthContext Effect2] Triggered with:', {
      user: user?.uid || 'null',
      hasUser: !!user,
      domainLoading,
      hasProjectId: !!domainConfig?.project_id,
      projectId: domainConfig?.project_id,
      lastFetchedProject: lastFetchedProjectRef.current
    });

    // Condizioni per fetchare il profilo
    if (user && !domainLoading && domainConfig?.project_id) {
      const currentProjectId = domainConfig.project_id;
      
      // Fetcha solo se è un progetto diverso
      if (lastFetchedProjectRef.current !== currentProjectId) {
        console.log('[AuthContext Effect2] All conditions met - Fetching profile for project:', currentProjectId);
        fetchUserProfile(user).then(profile => {
          console.log('[AuthContext Effect2] Profile fetch completed:', !!profile);
        }).catch(err => {
          console.error('[AuthContext Effect2] Profile fetch error:', err);
        });
      } else {
        console.log('[AuthContext Effect2] Skip fetch - already fetched for project:', currentProjectId);
      }
    } else {
      console.log('[AuthContext Effect2] Skip fetch - conditions not met:', {
        'has user': !!user,
        'domain not loading': !domainLoading,
        'has project_id': !!domainConfig?.project_id
      });
    }
  }, [user, domainLoading, domainConfig?.project_id]); // Dipendenze minime e stabili

  // ============= CONTEXT VALUE =============

  const contextValue = {
    // State
    user,
    userProfile,
    profile: userProfile, // ALIAS per compatibilità con componenti esistenti
    loading: loading, // SOLO auth loading, NON domain loading!
    error,
    authError: error, // ALIAS per compatibilità
    isAuthenticated: !!user && !!userProfile,
    isRegistering,
    needsProjectRegistration,
    isInitialized: !loading, // Per compatibilità con PublicRoute/ProtectedRoute
    projectAccess: userProfile ? { has_access: true, project_info: userProfile } : null, // Per compatibilità
    
    // Auth methods
    login,
    googleLogin,
    register,
    logout,
    resetPassword,
    
    // Profile methods
    refreshProfile: () => {
      lastFetchedProjectRef.current = null;
      return user ? fetchUserProfile(user) : null;
    },
    
    // Registrazione per progetto corrente
    registerForCurrentProject: async () => {
      if (!user || !domainConfig?.project_id) {
        console.error('[AuthContext] Cannot register: missing user or project_id');
        return null;
      }
      
      try {
        const token = await user.getIdToken();
        const projectId = domainConfig.project_id;
        const domainName = domainConfig.domain_name || currentDomain;
        
        console.log('[AuthContext] Registering user for current project:', projectId);
        
        const response = await fetch(`${BACKEND_URL}/api/user/register-project`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            'X-Domain-Name': domainName,
            'X-Project-ID': projectId,
            'X-Firebase-UID': user.uid
          },
          body: JSON.stringify({
            user_auth_provider_id: user.uid,
            user_email: user.email,
            user_display_name: user.displayName || user.email?.split('@')[0],
            user_photo_url: user.photoURL,
            domain_name: domainName,
            auth_provider_type: user.providerData?.[0]?.providerId || 'email',
            user_locale: navigator.language || 'it-IT'
          })
        });
        
        if (response.ok) {
          const profile = await response.json();
          console.log('[AuthContext] User registered successfully for project');
          setUserProfile(profile);
          setNeedsProjectRegistration(false);
          saveAuthData(token, profile);
          lastFetchedProjectRef.current = projectId;
          return { profile };
        } else {
          console.error('[AuthContext] Registration failed:', response.status);
          const errorData = await response.json().catch(() => ({}));
          console.error('[AuthContext] Error details:', errorData);
          return null;
        }
      } catch (error) {
        console.error('[AuthContext] Registration error:', error);
        return null;
      }
    },
    
    // Utility methods
    getValidToken,
    clearError: () => setError(null),
    canAccessCurrentProject,
    
    // Multi-tenant info
    currentProjectId: domainConfig?.project_id,
    currentDomain,
    isProjectConfigured: !!domainConfig?.project_id,
    domainLoading, // Esponi domainLoading separatamente se serve
    
    // DEBUG: metodo per testare
    debugState: () => {
      console.log('[AuthContext DEBUG]', {
        loading,
        domainLoading,
        user: !!user,
        userProfile: !!userProfile,
        projectId: domainConfig?.project_id,
        canNavigate: !loading && !domainLoading,
        needsProjectRegistration
      });
      return {
        canNavigate: !loading && !domainLoading,
        authReady: !loading,
        domainReady: !domainLoading
      };
    }
  };

  // Log del context value per debug
  console.log('[AuthContext] Context value:', {
    loading: contextValue.loading,
    authLoading: loading,
    domainLoading: domainLoading,
    hasUser: !!user,
    hasProfile: !!userProfile,
    isAuthenticated: contextValue.isAuthenticated
  });

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;